'client';

/* eslint-disable @typescript-eslint/no-explicit-any */
// This is a cleaned-up ES6 copy of the next/link component without prefetch from:
// https://github.com/vercel/next.js/blob/v12.2.5/packages/next/client/link.tsx
// This component was mainly copied to completely disable prefetch on link mouse-over
// Update this component if needed when updating Next.js

import React from 'react';
import type { NextRouter } from 'next/dist/shared/lib/router/router';
import { isLocalURL, resolveHref } from 'next/dist/shared/lib/router/router';
import { addLocale } from 'next/dist/client/add-locale';
import { RouterContext } from 'next/dist/shared/lib/router-context';
import type { AppRouterInstance } from 'next/dist/shared/lib/app-router-context';
import { AppRouterContext } from 'next/dist/shared/lib/app-router-context';
import { getDomainLocale } from 'next/dist/client/get-domain-locale';
import { addBasePath } from 'next/dist/client/add-base-path';

function isModifiedEvent(event: React.MouseEvent): boolean {
    const { target } = event.currentTarget as HTMLAnchorElement;
    return (
        (target && target !== '_self') ||
        event.metaKey || // open in new tab on macOS
        event.ctrlKey || // right-click on macOS, open in new tab on Windows
        event.shiftKey || // open in new window
        event.altKey || // triggers resource download
        (event.nativeEvent && event.nativeEvent.which === 2)
    );
}

function linkClicked(
    e: React.MouseEvent,
    router: NextRouter | AppRouterInstance,
    href: string,
    as: string,
    replace?: boolean,
    shallow?: boolean,
    scroll?: boolean,
    locale?: string | false,
    isAppRouter?: boolean
) {
    const { nodeName } = e.currentTarget;
    // anchors inside an svg have a lowercase nodeName
    const isAnchorNodeName = nodeName.toUpperCase() === 'A';
    if (isAnchorNodeName && (isModifiedEvent(e) || !isLocalURL(href))) {
        // ignore click for browser’s default behavior
        return;
    }
    e.preventDefault();

    const navigate = () => {
        // If the router is an NextRouter instance it will have `beforePopState`
        if ('beforePopState' in router) {
            router[replace ? 'replace' : 'push'](href, as, {
                shallow,
                locale,
                scroll
            });
        } else {
            // If `beforePopState` doesn't exist on the router it's the AppRouter.
            const method: keyof AppRouterInstance = replace
                ? 'replace'
                : 'push';

            router[method](href, { forceOptimisticNavigation: false });
        }
    };

    if (isAppRouter) {
        React.startTransition(navigate);
    } else {
        navigate();
    }
}

type InternalLinkProps = {
    href: string;
    as?: string;
    replace?: boolean;

    /**
     * TODO-APP
     */
    scroll?: boolean;
    shallow?: boolean;
    passHref?: boolean;
    prefetch?: boolean;
    locale?: string | false;
    legacyBehavior?: boolean;
    // e: any because as it would otherwise overlap with existing types
    /**
     * requires experimental.newNextLinkBehavior
     */
    onMouseEnter?: (e: any) => void;
    // e: any because as it would otherwise overlap with existing types
    /**
     * requires experimental.newNextLinkBehavior
     */
    onTouchStart?: (e: any) => void;
    // e: any because as it would otherwise overlap with existing types
    /**
     * requires experimental.newNextLinkBehavior
     */
    onClick?: (e: any) => void;
    children: React.ReactNode;
};

export const NextLink = React.forwardRef(function LinkComponent(
    props: InternalLinkProps,
    forwardedRef
) {
    const {
        href: hrefProp,
        as: asProp,
        children,
        passHref,
        replace,
        shallow,
        scroll,
        locale,
        onClick,
        onMouseEnter,
        onTouchStart,
        ...restProps
    } = props;
    let router = React.useContext(RouterContext);
    const appRouter = React.useContext(AppRouterContext) as any;
    if (appRouter) {
        router = appRouter;
    }
    const { href, as } = React.useMemo(() => {
        const [resolvedHref, resolvedAs] = resolveHref(
            router,
            hrefProp,
            true
        ) as unknown as [string, string];
        return {
            href: resolvedHref,
            as: asProp
                ? resolveHref(router, asProp)
                : resolvedAs || resolvedHref
        };
    }, [router, hrefProp, asProp]);

    const childProps: {
        onTouchStart: React.TouchEventHandler;
        onMouseEnter: React.MouseEventHandler;
        onClick: React.MouseEventHandler;
        href?: string;
        ref?: any;
    } = {
        ref: forwardedRef,
        onClick: (e) => {
            if (process.env.NODE_ENV !== 'production') {
                if (!e) {
                    throw new Error(
                        `Component rendered inside next/link has to pass click event to "onClick" prop.`
                    );
                }
            }
            if (typeof onClick === 'function') {
                onClick(e);
            }
            if (!e.defaultPrevented) {
                linkClicked(
                    e,
                    router,
                    href,
                    as,
                    replace,
                    shallow,
                    scroll,
                    locale,
                    Boolean(appRouter)
                );
            }
        },
        onMouseEnter: (e) => {
            if (typeof onMouseEnter === 'function') {
                onMouseEnter(e);
            }
        },
        onTouchStart: (e) => {
            if (typeof onTouchStart === 'function') {
                onTouchStart(e);
            }
        }
    };

    // If child is an <a> tag and doesn't have a href attribute, or if the 'passHref' property is
    // defined, we specify the current 'href', so that repetition is not needed by the user
    if (passHref) {
        const curLocale =
            typeof locale !== 'undefined' ? locale : router && router.locale;
        // we only render domain locales if we are currently on a domain locale
        // so that locale links are still visitable in development/preview envs
        const localeDomain =
            router &&
            router.isLocaleDomain &&
            getDomainLocale(
                as,
                curLocale,
                router.locales,
                router.domainLocales
            );
        childProps.href =
            localeDomain ||
            addBasePath(
                addLocale(as, curLocale, router && router.defaultLocale)
            );
    }

    return React.createElement(
        'a',
        Object.assign({}, restProps, childProps),
        children
    );
});
